import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  // GETs for the same URL that are already on their way are shared. A page that
  // asks twice while loading (React mounts effects twice in development; some
  // pages load from more than one place) then gets ONE answer at ONE moment —
  // never a second, later copy that overwrites what someone has started typing.
  inFlight = new Map();

  request(endpoint, options = {}) {
    const method = options.method || 'GET';
    if (method !== 'GET' || options.body || options.headers) return this.send(endpoint, options);
    const key = `${localStorage.getItem(TOKEN_STORAGE_KEY) || ''} ${endpoint}`;
    if (!this.inFlight.has(key)) {
      const pending = this.send(endpoint, options).finally(() => this.inFlight.delete(key));
      this.inFlight.set(key, pending);
    }
    return this.inFlight.get(key);
  }

  async send(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  changePassword({ currentPassword, newPassword }) {
    return this.request('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } });
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- RBAC: managed accounts ----
  listAccounts({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users/accounts?limit=${limit}&offset=${offset}`);
  }

  createAccount({ name, email, status, roleIds, tugId }) {
    return this.request('/users/accounts', {
      method: 'POST',
      body: { name, email, status, roleIds, tugId },
    });
  }

  updateAccount(id, { name, email, status, roleIds, tugId }) {
    return this.request(`/users/accounts/${id}`, {
      method: 'PUT',
      body: { name, email, status, roleIds, tugId },
    });
  }

  // ---- RBAC: roles + Security Policy Matrix ----
  listRoles() {
    return this.request('/rbac/roles');
  }

  listPermissions() {
    return this.request('/rbac/permissions');
  }

  updateRolePermission(roleId, perms) {
    return this.request(`/rbac/permissions/${roleId}`, { method: 'PUT', body: perms });
  }

  // ---- Harbor geography master data ----
  listPorts() {
    return this.request('/geography/ports');
  }
  createPort({ name, code, boundary }) {
    return this.request('/geography/ports', { method: 'POST', body: { name, code, boundary } });
  }
  updatePort(id, { name, code, boundary }) {
    return this.request(`/geography/ports/${id}`, { method: 'PUT', body: { name, code, boundary } });
  }
  deletePort(id) {
    return this.request(`/geography/ports/${id}`, { method: 'DELETE' });
  }

  listBerths(portId) {
    return this.request(`/geography/berths?portId=${portId}`);
  }
  createBerth(portId, { name, lengthM, draftM }) {
    return this.request(`/geography/ports/${portId}/berths`, {
      method: 'POST',
      body: { name, lengthM, draftM },
    });
  }
  deleteBerth(id) {
    return this.request(`/geography/berths/${id}`, { method: 'DELETE' });
  }

  listStations() {
    return this.request('/geography/stations');
  }
  createStation({ name, latitude, longitude }) {
    return this.request('/geography/stations', {
      method: 'POST',
      body: { name, latitude, longitude },
    });
  }
  deleteStation(id) {
    return this.request(`/geography/stations/${id}`, { method: 'DELETE' });
  }

  // ---- Owned tugs ----
  listTugs() {
    return this.request('/tugs');
  }
  createTug(payload) {
    return this.request('/tugs', { method: 'POST', body: payload });
  }
  updateTug(id, payload) {
    return this.request(`/tugs/${id}`, { method: 'PUT', body: payload });
  }
  deleteTug(id) {
    return this.request(`/tugs/${id}`, { method: 'DELETE' });
  }
  listTugMaintenance(id) {
    return this.request(`/tugs/${id}/maintenance`);
  }
  createTugMaintenance(id, payload) {
    return this.request(`/tugs/${id}/maintenance`, { method: 'POST', body: payload });
  }
  completeTugMaintenance(id, eventId, delivery) {
    return this.request(`/tugs/${id}/maintenance/${eventId}/complete`, { method: 'POST', body: delivery || {} });
  }
  getMaintenancePlans() {
    return this.request('/tugs/maintenance-plans');
  }
  createMaintenancePlan(payload) {
    return this.request('/tugs/maintenance-plans', { method: 'POST', body: payload });
  }
  getBreakdownLog() {
    return this.request('/tugs/breakdowns');
  }
  getTugTimeline() {
    return this.request('/tugs/timeline');
  }

  // ---- Vessel master registry ----
  listVessels(search) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/vessels${qs}`);
  }
  listVesselCustomers() {
    return this.request('/vessels/customers');
  }
  createVessel(payload) {
    return this.request('/vessels', { method: 'POST', body: payload });
  }
  updateVessel(id, payload) {
    return this.request(`/vessels/${id}`, { method: 'PUT', body: payload });
  }
  deleteVessel(id) {
    return this.request(`/vessels/${id}`, { method: 'DELETE' });
  }
  aisLookup(mmsi) {
    return this.request(`/vessels/ais/${mmsi}`);
  }

  // ---- Port calls & AIS ETA-drift tracking ----
  listPortCalls(search) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/port-calls${qs}`);
  }
  createPortCall(payload) {
    return this.request('/port-calls', { method: 'POST', body: payload });
  }
  ingestPortCallAis(id, payload) {
    return this.request(`/port-calls/${id}/ais`, { method: 'POST', body: payload });
  }

  // ---- Live tracking map (GPS/AIS + geofence logs) ----
  getTrackingSnapshot() {
    return this.request('/tracking/snapshot');
  }
  recordGeofence(positionId, payload) {
    return this.request(`/tracking/positions/${positionId}/geofence`, {
      method: 'POST',
      body: payload,
    });
  }

  // ---- Fleet scheduler Gantt ----
  getSchedulerGantt(date) {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.request(`/scheduler/gantt${qs}`);
  }
  reassignSchedulerJob(jobId, payload) {
    return this.request(`/scheduler/jobs/${jobId}/reassign`, { method: 'POST', body: payload });
  }

  // ---- Operational exceptions & SLA watch ----
  getExceptions() {
    return this.request('/exceptions');
  }
  getExceptionBackupTugs(jobId) {
    return this.request(`/exceptions/${jobId}/backup-tugs`);
  }
  contactExceptionTug(jobId) {
    return this.request(`/exceptions/${jobId}/contact`, { method: 'POST' });
  }
  reassignExceptionJob(jobId, payload) {
    return this.request(`/exceptions/${jobId}/reassign`, { method: 'POST', body: payload });
  }

  // ---- Regulatory PPA compliance reports ----
  getPpaReportOptions() {
    return this.request('/reports/ppa/options');
  }
  getOperationsMetrics(period) {
    const qs = period ? `?period=${encodeURIComponent(period)}` : '';
    return this.request(`/reports/metrics${qs}`);
  }
  getHoursByMovement() {
    return this.request('/reports/hours-by-movement');
  }
  getCapacityForecast(days) {
    return this.request(`/dispatch/capacity-forecast${days ? `?days=${days}` : ''}`);
  }

  // ---- Commercial revenues & costs ----
  getCommercialOptions() {
    return this.request('/commercial/options');
  }
  getCommercialDashboard({ customer, portId, from, to } = {}) {
    const params = {};
    if (customer) params.customer = customer;
    if (portId) params.portId = portId;
    if (from) params.from = from;
    if (to) params.to = to;
    const qs = new URLSearchParams(params).toString();
    return this.request(`/commercial/dashboard${qs ? `?${qs}` : ''}`);
  }
  generatePpaReport({ portId, from, to, thrusterFactorAllowed }) {
    const qs = new URLSearchParams({
      portId,
      from,
      to,
      thrusterFactorAllowed: String(thrusterFactorAllowed),
    }).toString();
    return this.request(`/reports/ppa?${qs}`);
  }

  // ---- Customer self-registration (public) ----
  registerCustomer(payload) {
    return this.request('/customer-registrations', { method: 'POST', body: payload });
  }
  listCustomerRegistrations(status) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/customer-registrations${qs}`);
  }
  approveRegistration(id, sapCustomerId) {
    return this.request(`/customer-registrations/${id}/approve`, {
      method: 'POST',
      body: { sapCustomerId },
    });
  }
  rejectRegistration(id, reason) {
    return this.request(`/customer-registrations/${id}/reject`, {
      method: 'POST',
      body: { reason },
    });
  }

  // ---- Tariff rate cards ----
  listRateCards() {
    return this.request('/tariffs/rate-cards');
  }
  createRateCard(payload) {
    return this.request('/tariffs/rate-cards', { method: 'POST', body: payload });
  }
  getTariffCustomers() {
    return this.request('/tariffs/customers');
  }
  getTariffPenalties() {
    return this.request('/tariffs/penalties');
  }
  updateTariffPenalties(payload) {
    return this.request('/tariffs/penalties', { method: 'PUT', body: payload });
  }

  // ---- Allocation rules + compulsory floors ----
  listAllocationRules() {
    return this.request('/allocation/rules');
  }
  createAllocationRule(payload) {
    return this.request('/allocation/rules', { method: 'POST', body: payload });
  }
  deleteAllocationRule(id) {
    return this.request(`/allocation/rules/${id}`, { method: 'DELETE' });
  }
  checkAllocation({ gt, tugCount }) {
    return this.request(`/allocation/check?gt=${gt}&tugCount=${tugCount}`);
  }

  // ---- Customer bookings ----
  /** Office staff pass the customer they are booking for, to get that customer's vessels. */
  getBookingReference(customerUserId) {
    const qs = customerUserId ? `?customerUserId=${encodeURIComponent(customerUserId)}` : '';
    return this.request(`/bookings/reference${qs}`);
  }
  getBookingQuote({ requestedTugCount, vesselId, movementType, serviceId, portId, assistanceAreaId, customerUserId } = {}) {
    const params = {};
    if (customerUserId) params.customerUserId = customerUserId;
    if (requestedTugCount) params.requestedTugCount = requestedTugCount;
    if (vesselId) params.vesselId = vesselId;
    if (movementType) params.movementType = movementType;
    // 8949 / 8950: the service, port and area pick the service tariff.
    if (serviceId) params.serviceId = serviceId;
    if (portId) params.portId = portId;
    if (assistanceAreaId) params.assistanceAreaId = assistanceAreaId;
    const qs = new URLSearchParams(params).toString();
    return this.request(`/bookings/quote${qs ? `?${qs}` : ''}`);
  }
  listBookings() {
    return this.request('/bookings');
  }
  createBooking(payload) {
    return this.request('/bookings', { method: 'POST', body: payload });
  }
  confirmBooking(id, payload) {
    return this.request(`/bookings/${id}/confirm`, { method: 'POST', body: payload });
  }
  cancelBooking(id, payload) {
    return this.request(`/bookings/${id}/cancel`, { method: 'POST', body: payload });
  }
  getCancellationCharges() {
    return this.request('/bookings/cancellation-charges');
  }
  getRecurringBookings() {
    return this.request('/bookings/recurring');
  }
  createRecurringBooking(payload) {
    return this.request('/bookings/recurring', { method: 'POST', body: payload });
  }
  // 9025: edit, pause / resume and delete a standing order.
  updateRecurringBooking(id, payload) {
    return this.request(`/bookings/recurring/${id}`, { method: 'PUT', body: payload });
  }
  pauseRecurringBooking(id) {
    return this.request(`/bookings/recurring/${id}/pause`, { method: 'POST' });
  }
  resumeRecurringBooking(id) {
    return this.request(`/bookings/recurring/${id}/resume`, { method: 'POST' });
  }
  deleteRecurringBooking(id) {
    return this.request(`/bookings/recurring/${id}`, { method: 'DELETE' });
  }
  getBookingExecution(id) {
    return this.request(`/bookings/${id}/execution`);
  }
  /** Ship's master / agent: confirm the assist was performed. */
  confirmAssist(id, name) {
    return this.request(`/bookings/${id}/confirm-assist`, { method: 'POST', body: { name } });
  }
  /** Dispatcher: record the ship's master's confirmation of one job ticket on their behalf (8991). */
  confirmAssistForMaster(ticketId, payload) {
    return this.request(`/dispatch/job-tickets/${ticketId}/confirm-assist`, { method: 'POST', body: payload });
  }

  // ---- Customer profile: favorite vessels & preferences ----
  getProfileOptions() {
    return this.request('/profile/options');
  }
  listFavoriteVessels() {
    return this.request('/profile/vessels');
  }
  addFavoriteVessel(payload) {
    return this.request('/profile/vessels', { method: 'POST', body: payload });
  }
  updateFavoriteVessel(id, payload) {
    return this.request(`/profile/vessels/${id}`, { method: 'PUT', body: payload });
  }
  deleteFavoriteVessel(id) {
    return this.request(`/profile/vessels/${id}`, { method: 'DELETE' });
  }
  getPreferences() {
    return this.request('/profile/preferences');
  }
  savePreferences(payload) {
    return this.request('/profile/preferences', { method: 'PUT', body: payload });
  }

  // ---- Assisted bookings (order triaging) ----
  getAssistedQueue() {
    return this.request('/assisted-bookings/queue');
  }
  getAssistedDraft(id) {
    return this.request(`/assisted-bookings/queue/${id}`);
  }
  createAssistedBooking(id, payload) {
    return this.request(`/assisted-bookings/queue/${id}/create`, { method: 'POST', body: payload });
  }
  discardAssistedDraft(id) {
    return this.request(`/assisted-bookings/queue/${id}/discard`, { method: 'POST' });
  }

  // ---- External integrations & notification settings (admin) ----
  getIntegrationSettings() {
    return this.request('/settings/integrations');
  }
  testIntegrationConnection(payload) {
    return this.request('/settings/integrations/test', { method: 'POST', body: payload });
  }
  saveIntegrationSettings(payload) {
    return this.request('/settings/integrations', { method: 'PUT', body: payload });
  }
  getNotificationSettings() {
    return this.request('/settings/notifications');
  }
  saveNotificationSettings(payload) {
    return this.request('/settings/notifications', { method: 'PUT', body: payload });
  }
  getSaasBillingSettings() {
    return this.request('/settings/saas-billing');
  }
  saveSaasBillingSettings(payload) {
    return this.request('/settings/saas-billing', { method: 'PUT', body: payload });
  }
  getSaasUsage(period) {
    const qs = period ? `?period=${encodeURIComponent(period)}` : '';
    return this.request(`/settings/saas-billing/usage${qs}`);
  }
  getSaasInvoices() {
    return this.request('/settings/saas-billing/invoices');
  }
  sendSaasInvoice(payload) {
    return this.request('/settings/saas-billing/invoices', { method: 'POST', body: payload });
  }

  // ---- Immutable audit ledger (admin) ----
  getAuditCategories() {
    return this.request('/audit-logs/categories');
  }
  searchAuditLogs({ search, category, from, to } = {}) {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    if (from) params.from = from;
    if (to) params.to = to;
    const qs = new URLSearchParams(params).toString();
    return this.request(`/audit-logs${qs ? `?${qs}` : ''}`);
  }
  /** Fetches the authed SoF PDF as a Blob (cannot use a plain anchor link). */
  async downloadSofPdf(id) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/bookings/${id}/sof.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const payload = await res.json();
        message = payload?.error?.message || message;
      } catch {
        // non-JSON error body
      }
      throw new Error(message);
    }
    return res.blob();
  }

  // ---- Crew roster + certifications ----
  listCrewMembers() {
    return this.request('/crew/members');
  }
  listCrewMovements(date) {
    return this.request(`/crew/movements${date ? `?date=${date}` : ''}`);
  }
  addCrewMovement(payload) {
    return this.request('/crew/movements', { method: 'POST', body: payload });
  }
  deleteCrewMovement(id) {
    return this.request(`/crew/movements/${id}`, { method: 'DELETE' });
  }
  createCrewMember(payload) {
    return this.request('/crew/members', { method: 'POST', body: payload });
  }
  updateCrewMember(id, payload) {
    return this.request(`/crew/members/${id}`, { method: 'PUT', body: payload });
  }
  listCaptainLogins() {
    return this.request('/crew/captain-logins');
  }
  listCrewShifts() {
    return this.request('/crew/shifts');
  }
  assignCrewShift(payload) {
    return this.request('/crew/shifts', { method: 'POST', body: payload });
  }
  updateCrewShift(id, payload) {
    return this.request(`/crew/shifts/${id}`, { method: 'PUT', body: payload });
  }
  triggerCaptainExpiryAlert(memberId) {
    return this.request(`/crew/members/${memberId}/expiry-alert`, { method: 'POST' });
  }

  // ---- Dispatch planning ----
  listConfirmedBookings() {
    return this.request('/dispatch/confirmed-bookings');
  }
  // 8952-8954: plan and dispatch job by job (1 job = 1 tug = 1 ticket).
  listJobQueue() {
    return this.request('/dispatch/job-queue');
  }
  getJobPlanning(jobId, at) {
    return this.request(`/dispatch/jobs/${jobId}/planning${at ? `?at=${encodeURIComponent(at)}` : ''}`);
  }
  saveJobPlan(jobId, payload) {
    return this.request(`/dispatch/jobs/${jobId}/plan`, { method: 'PUT', body: payload });
  }
  dispatchJob(jobId, payload) {
    return this.request(`/dispatch/jobs/${jobId}/dispatch`, { method: 'POST', body: payload });
  }
  addBookingJob(bookingId, payload = {}) {
    return this.request(`/dispatch/bookings/${bookingId}/jobs`, { method: 'POST', body: payload });
  }
  // 9033: replace the tug on a planned/dispatched job, or cancel one job.
  getReplacementOptions(jobId) {
    return this.request(`/dispatch/jobs/${jobId}/replacement-options`);
  }
  replaceJobTug(jobId, payload) {
    return this.request(`/dispatch/jobs/${jobId}/replace-tug`, { method: 'POST', body: payload });
  }
  cancelBookingJob(jobId, payload) {
    return this.request(`/dispatch/jobs/${jobId}/cancel`, { method: 'POST', body: payload });
  }
  listJobTickets() {
    return this.request('/dispatch/job-tickets');
  }
  reassignJob(id, payload) {
    return this.request(`/dispatch/job-tickets/${id}/reassign`, { method: 'POST', body: payload });
  }
  // The captain's notices: a job taken off them or reassigned to them.
  getCaptainNotices() {
    return this.request('/dispatch/captain-notices');
  }
  dismissCaptainNotice(id) {
    return this.request(`/dispatch/captain-notices/${id}/read`, { method: 'POST' });
  }
  acknowledgeJob(id) {
    return this.request(`/dispatch/job-tickets/${id}/acknowledge`, { method: 'POST' });
  }

  // ---- Captain execution PWA ----
  // 8955: the captain's current job, or the one they picked.
  getActiveTicket(jobId) {
    return this.request(`/execution/active-ticket${jobId ? `?jobId=${jobId}` : ''}`);
  }
  logClock(jobId, payload) {
    return this.request(`/execution/jobs/${jobId}/clock`, { method: 'POST', body: payload });
  }
  logMilestone(jobId, payload) {
    return this.request(`/execution/jobs/${jobId}/milestones`, { method: 'POST', body: payload });
  }
  getExecutionLog(jobId) {
    return this.request(`/execution/jobs/${jobId}/log`);
  }
  saveRemarks(jobId, remarks) {
    return this.request(`/execution/jobs/${jobId}/remarks`, { method: 'PUT', body: { remarks } });
  }
  getMyDtr(date, tugId) {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (tugId) q.set('tugId', tugId);
    return this.request(`/dtr/mine?${q}`);
  }
  saveDtrSection(section, body, date, tugId, ifSavedAt) {
    const q = new URLSearchParams();
    if (date) q.set('date', date);
    if (tugId) q.set('tugId', tugId);
    if (ifSavedAt !== undefined) q.set('ifSavedAt', ifSavedAt);
    return this.request(`/dtr/mine/${section}?${q}`, { method: 'PUT', body });
  }
  /** Office: one tug's report for a date, with its correction history. */
  getOfficeDtr(tugId, date) {
    return this.request(`/dtr/report/${tugId}${date ? `?date=${date}` : ''}`);
  }
  correctDtrSection(section, body, date, tugId, reason) {
    return this.request(`/dtr/report/${tugId}/${section}${date ? `?date=${date}` : ''}`, {
      method: 'PUT',
      body: { ...body, reason },
    });
  }
  getDtrList(date) {
    return this.request(`/dtr${date ? `?date=${date}` : ''}`);
  }
  getDtrMissing() {
    return this.request('/dtr/missing');
  }
  getPortConditions(date) {
    return this.request(`/dtr/conditions${date ? `?date=${date}` : ''}`);
  }
  savePortConditions(portId, body, date) {
    return this.request(`/dtr/conditions/${portId}${date ? `?date=${date}` : ''}`, { method: 'PUT', body });
  }
  getDailyReportSettings() {
    return this.request('/settings/daily-report');
  }
  // ---- Inventory transfers ----
  getInventoryLocations() {
    return this.request('/inventory/locations');
  }
  addInventoryStore(payload) {
    return this.request('/inventory/stores', { method: 'POST', body: payload });
  }
  getInventoryPending(tugId) {
    return this.request(`/inventory/pending${tugId ? `?tugId=${tugId}` : ''}`);
  }
  getInventoryMovements(date) {
    return this.request(`/inventory/movements${date ? `?date=${date}` : ''}`);
  }
  getInventorySuggestions(tugId) {
    return this.request(`/inventory/suggestions${tugId ? `?tugId=${tugId}` : ''}`);
  }
  createInventoryTransfer(payload) {
    return this.request('/inventory/transfers', { method: 'POST', body: payload });
  }
  receiveInventory(id, payload) {
    return this.request(`/inventory/transfers/${id}/receive`, { method: 'POST', body: payload });
  }
  recordInventoryUsed(payload) {
    return this.request('/inventory/used', { method: 'POST', body: payload });
  }
  // ---- Safety ----
  listSafetyIncidents() {
    return this.request('/safety/incidents');
  }
  reportSafetyIncident(payload) {
    return this.request('/safety/incidents', { method: 'POST', body: payload });
  }
  deleteSafetyIncident(id) {
    return this.request(`/safety/incidents/${id}`, { method: 'DELETE' });
  }
  getSafetySettings() {
    return this.request('/safety/settings');
  }
  saveSafetySettings(payload) {
    return this.request('/safety/settings', { method: 'PUT', body: payload });
  }
  // ---- Daily management report ----
  getDailyReport(date) {
    return this.request(`/daily-report${date ? `?date=${date}` : ''}`);
  }
  sendDailyReport(date) {
    return this.request('/daily-report/send', { method: 'POST', body: { date } });
  }
  /** Authenticated fetch for non-JSON responses (HTML preview, workbook download). */
  async rawGet(endpoint) {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_STORAGE_KEY)}` },
    });
    if (!res.ok) {
      const payload = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return res;
  }
  async getDailyReportPreview(date) {
    return (await this.rawGet(`/daily-report/preview?date=${date}`)).text();
  }
  async downloadDailyReportWorkbook(date) {
    const res = await this.rawGet(`/daily-report/workbook?date=${date}`);
    return { blob: await res.blob(), filename: `TugOS-Daily-Report-${date}.xlsx` };
  }
  sendDailyReportTest(to) {
    return this.request('/settings/daily-report/test', { method: 'POST', body: { to } });
  }
  saveDailyReportSettings(body) {
    return this.request('/settings/daily-report', { method: 'PUT', body });
  }
  getDtrTugs() {
    return this.request('/dtr/tugs');
  }
  getSof(jobId) {
    return this.request(`/execution/jobs/${jobId}/sof`);
  }
  logDelay(jobId, payload) {
    return this.request(`/execution/jobs/${jobId}/delays`, { method: 'POST', body: payload });
  }
  submitSof(jobId, payload) {
    return this.request(`/execution/jobs/${jobId}/sof/submit`, { method: 'POST', body: payload });
  }

  // ---- Billing: audited timestamp corrections ----
  listBillingJobs() {
    return this.request('/billing/jobs');
  }
  getTimesheet(jobId) {
    return this.request(`/billing/timesheet/${jobId}`);
  }
  getAuditLedger(jobId) {
    return this.request(`/billing/audit/${jobId}`);
  }
  adjustClock(id, payload) {
    return this.request(`/billing/clock/${id}`, { method: 'PUT', body: payload });
  }
  adjustMilestone(id, payload) {
    return this.request(`/billing/milestone/${id}`, { method: 'PUT', body: payload });
  }

  // ---- Service master, assistance areas, service rates (8947-8951) ----
  listServices(params = {}) {
    const q = params.active !== undefined ? `?active=${params.active}` : '';
    return this.request(`/services${q}`);
  }
  createService(payload) {
    return this.request('/services', { method: 'POST', body: payload });
  }
  updateService(id, payload) {
    return this.request(`/services/${id}`, { method: 'PUT', body: payload });
  }
  deleteService(id) {
    return this.request(`/services/${id}`, { method: 'DELETE' });
  }
  listServicePorts() {
    return this.request('/services/ports');
  }
  listAssistanceAreas(portId) {
    return this.request(`/services/areas${portId ? `?portId=${portId}` : ''}`);
  }
  createAssistanceArea(payload) {
    return this.request('/services/areas', { method: 'POST', body: payload });
  }
  updateAssistanceArea(id, payload) {
    return this.request(`/services/areas/${id}`, { method: 'PUT', body: payload });
  }
  deleteAssistanceArea(id) {
    return this.request(`/services/areas/${id}`, { method: 'DELETE' });
  }
  listServiceRates() {
    return this.request('/services/rates');
  }
  createServiceRate(payload) {
    return this.request('/services/rates', { method: 'POST', body: payload });
  }
  updateServiceRate(id, payload) {
    return this.request(`/services/rates/${id}`, { method: 'PUT', body: payload });
  }
  deleteServiceRate(id) {
    return this.request(`/services/rates/${id}`, { method: 'DELETE' });
  }

  // ---- Invoicing drafts / billing approval ----
  listInvoiceDrafts() {
    return this.request('/invoicing/drafts');
  }
  getInvoiceDraft(jobId) {
    return this.request(`/invoicing/drafts/${jobId}`);
  }
  setSpecialRate(jobId, payload) {
    return this.request(`/invoicing/drafts/${jobId}/special-rate`, { method: 'PUT', body: payload });
  }
  clearSpecialRate(jobId) {
    return this.request(`/invoicing/drafts/${jobId}/special-rate`, { method: 'DELETE' });
  }
  approveInvoice(jobId) {
    return this.request(`/invoicing/drafts/${jobId}/approve`, { method: 'POST' });
  }
  // 8956: one consolidated sales invoice per booking.
  listBookingInvoices() {
    return this.request('/invoicing/invoices');
  }
  getBookingInvoice(bookingId) {
    return this.request(`/invoicing/invoices/${bookingId}`);
  }
  approveBookingInvoice(bookingId) {
    return this.request(`/invoicing/invoices/${bookingId}/approve`, { method: 'POST' });
  }

  // ---- SAP integration queue ----
  getSapQueue() {
    return this.request('/sap/queue');
  }
  getSapErrors() {
    return this.request('/sap/errors');
  }
  raiseSap(jobId) {
    return this.request(`/sap/raise/${jobId}`, { method: 'POST' });
  }
  retrySap(jobId) {
    return this.request(`/sap/retry/${jobId}`, { method: 'POST' });
  }

  // ---- Affiliate portal ----
  getAffiliateProfile() {
    return this.request('/affiliate/profile');
  }
  updateAffiliateProfile(payload) {
    return this.request('/affiliate/profile', { method: 'PUT', body: payload });
  }
  getAffiliateStations() {
    return this.request('/affiliate/stations');
  }
  listAffiliateAssets() {
    return this.request('/affiliate/assets');
  }
  createAffiliateAsset(payload) {
    return this.request('/affiliate/assets', { method: 'POST', body: payload });
  }
  deleteAffiliateAsset(id) {
    return this.request(`/affiliate/assets/${id}`, { method: 'DELETE' });
  }

  // ---- Affiliate broadcasts ----
  getBroadcastEligible(bookingId) {
    return this.request(`/broadcasts/eligible/${bookingId}`);
  }
  createBroadcast(payload) {
    return this.request('/broadcasts', { method: 'POST', body: payload });
  }
  listBroadcasts() {
    return this.request('/broadcasts');
  }
  getBroadcastOffers() {
    return this.request('/broadcasts/offers');
  }
  getMyBroadcastJobs() {
    return this.request('/broadcasts/my-jobs');
  }
  acceptOffer(targetId) {
    return this.request(`/broadcasts/offers/${targetId}/accept`, { method: 'POST' });
  }
  declineOffer(targetId) {
    return this.request(`/broadcasts/offers/${targetId}/decline`, { method: 'POST' });
  }

  // ---- Affiliate inter-company cost audit ----
  listSubcontractCosts() {
    return this.request('/intercompany/subcontracts');
  }
  getSubcontractCost(jobId) {
    return this.request(`/intercompany/subcontracts/${jobId}`);
  }
  approveSubcontractCost(jobId, payload) {
    return this.request(`/intercompany/subcontracts/${jobId}/approve`, {
      method: 'POST',
      body: payload,
    });
  }
  raiseSubcontractToSap(jobId) {
    return this.request(`/intercompany/subcontracts/${jobId}/raise-sap`, { method: 'POST' });
  }

  // ---- Pilot coordination ----
  listPilotBookings() {
    return this.request('/pilot/bookings');
  }
  listAccreditedPilots() {
    return this.request('/pilot/accredited');
  }
  getPilotMarketplace() {
    return this.request('/pilot/marketplace');
  }
  getMyPilotProfile() {
    return this.request('/pilot/me');
  }
  getMyPilotAssignments() {
    return this.request('/pilot/my-assignments');
  }
  assignPilotage(payload) {
    return this.request('/pilot/assign', { method: 'POST', body: payload });
  }
  acceptPilotRequest(assignmentId) {
    return this.request(`/pilot/marketplace/${assignmentId}/accept`, { method: 'POST' });
  }
}

export default new ApiService();
